import { useState, useRef, useEffect } from "react";
import { Button } from "../../../../components/ui/button";
import { Textarea } from "../../../../components/ui/textarea";
import { Pencil, Check, X } from "lucide-react";

interface InlineEditableTextProps {
  value: string;
  onSave: (newValue: string) => Promise<void>;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

export const InlineEditableText = ({
  value,
  onSave,
  placeholder = "Click to edit...",
  className = "",
  disabled = false,
}: InlineEditableTextProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const [isSaving, setIsSaving] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    setEditValue(value);
  }, [value]);

  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.select();
    }
  }, [isEditing]);

  const handleSave = async () => {
    if (editValue === value) {
      setIsEditing(false);
      return;
    }

    setIsSaving(true);
    try {
      await onSave(editValue);
      setIsEditing(false);
    } catch (error) {
      console.error("Error saving:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setEditValue(value);
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      handleCancel();
    } else if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      handleSave();
    }
  };

  if (isEditing) {
    return (
      <div className={`space-y-2 ${className}`}>
        <Textarea
          ref={textareaRef}
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="min-h-[100px] resize-y"
          disabled={isSaving}
        />
        <div className="flex gap-2">
          <Button
            size="sm"
            onClick={handleSave}
            disabled={isSaving}
            className="flex items-center gap-1"
          >
            <Check className="h-3 w-3" />
            Save
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={handleCancel}
            disabled={isSaving}
            className="flex items-center gap-1"
          >
            <X className="h-3 w-3" />
            Cancel
          </Button>
        </div>
        <div className="text-xs text-font-secondary">
          Press Esc to cancel, Cmd/Ctrl+Enter to save
        </div>
      </div>
    );
  }

  return (
    <div
      className={`group relative cursor-pointer rounded p-2 hover:bg-stone-50 transition-colors ${className} ${
        disabled ? "cursor-default" : ""
      }`}
      onClick={() => !disabled && setIsEditing(true)}
    >
      <div className="flex items-start gap-2">
        <div className="flex-1 whitespace-pre-wrap break-words">
          {value || (
            <span className="text-font-secondary italic">{placeholder}</span>
          )}
        </div>
        {!disabled && (
          <Pencil className="h-3 w-3 text-font-secondary opacity-0 group-hover:opacity-100 transition-opacity shrink-0 mt-1" />
        )}
      </div>
    </div>
  );
};
